import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IntegrationService } from '../../../services/integration.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { RestaurantService } from '../../../services/restaurant.service';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../models/response';
import { UberEatsDeliveryComponent } from './modal/uber-eats-delivery/uber-eats-delivery.component';
import { UberEatsSetupComponent } from './modal/uber-eats-setup/uber-eats-setup.component';
import { Router } from '@angular/router';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isPosManaged } from '../../../utils/pos-provider.util';

interface UpcomingPartner {
  name: string;
  initial: string;
  color: string;
  description: string;
  logo?: string;
  /** Wordmark logos render in an aspect-preserving box instead of the square chip. */
  wideLogo?: boolean;
}
@Component({
  selector: 'app-delivery-partners',
  standalone: true,
  imports: [MatTooltipModule],
  templateUrl: './delivery-partners.component.html',
  styleUrl: './delivery-partners.component.css',
})
export class DeliveryPartnersComponent {
  integrationService = inject(IntegrationService);
  ngxSpinnerService = inject(NgxSpinnerService);
  restaurantService = inject(RestaurantService);
  toastr = inject(ToastrService);
  router = inject(Router);
  uberEatsAuthDetails: any;
  currentRestaurant: any;
  uberEatsRestusurentStatus: boolean = false;
  uberEatsStoreName: string = '';
  isShowUberEatsCard: boolean = false;
  restaurantPosProvider: string = 'fnf';

  /**
   * Partners shown in the design that have no integration behind them yet —
   * listed so the roadmap is visible, without offering a Connect button that
   * would do nothing.
   */
  readonly upcomingPartners: UpcomingPartner[] = [
    {
      name: 'DoorDash',
      initial: 'D',
      color: '#eb1700',
      logo: 'assets/doordash.png',
      description: 'Connect DoorDash to manage deliveries and sync orders directly with your POS.',
    },
    {
      name: 'Grubhub',
      initial: 'G',
      color: '#f63440',
      logo: 'assets/grubhub.png',
      description: 'Sync your Grubhub orders and menu items for streamlined delivery management.',
    },
  ];

  /** Partners whose logo file failed to load, so the row falls back to the letter chip. */
  failedLogos: Record<string, boolean> = {};

  onLogoError(partnerName: string) {
    this.failedLogos[partnerName] = true;
  }

  constructor(private dialog: MatDialog) {}

  ngOnInit() {
    this.getUberEatsRestusurentStatus();
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.currentRestaurant = restaurant;
    });
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      if (flags && !isFeatureActive(flags, FEATURE_FLAG_KEYS.DELIVERY_PARTNERS)) {
        this.router.navigate(['/']);
        return;
      }
      this.isShowUberEatsCard = isFeatureActive(flags, FEATURE_FLAG_KEYS.UBER);
    });
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
      if (isPosManaged(this.restaurantPosProvider)) {
        this.router.navigate(['/']);
      }
    });
  }

  getUberEatsAuthorizationDetails() {
    this.ngxSpinnerService.show();
    this.integrationService.getUberEatsAuthorization().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.uberEatsAuthDetails = response.data;
        this.ngxSpinnerService.hide();
        this.enableUberEats();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  enableUberEats() {
    const dialogRef = this.dialog.open(UberEatsDeliveryComponent, {
      data: { uberEatsAuthDetails: this.uberEatsAuthDetails },
      width: '450px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
        if (result === 'finishSetup') {
          this.getUberEatsRestusurentStatus();
        }
      }
    });
  }

  openUberEatsSetup() {
    if (!this.uberEatsRestusurentStatus) {
      this.toastr.error('Uber Eats is not connected!', 'Error');
      return;
    }
    const dialogRef = this.dialog.open(UberEatsSetupComponent, {
      data: { uberEatsAuthDetails: this.uberEatsAuthDetails },
      width: '550px',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
        if (result === 'finishSetup') {
        }
      }
    });
  }

  getUberEatsRestusurentStatus() {
    this.ngxSpinnerService.show();
    this.integrationService.uberEatsRestusurentStaus().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.uberEatsRestusurentStatus = response.data?.completed || false;
        this.uberEatsStoreName = response.data?.store_name || '';
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  disconnectConfirmation() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: 'Are you sure you want to disconnect Uber Eats?' },
      width: '405px',
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result !== '') {
        if (result) {
          this.disconnectUberEats();
        }
      }
    });
  }

  disconnectUberEats() {
    this.ngxSpinnerService.show();
    this.integrationService.disconnectUberEats().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.toastr.success('Uber Eats disconnected successfully', 'Success');
        this.ngxSpinnerService.hide();
        this.getUberEatsRestusurentStatus();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }
}
